import { useLoaderData, Outlet, useNavigate } from "react-router";
import { Box, Stack } from "@mui/material";
import { BailleurItem, CommuneItem, LogItem } from "../../services/ApiService";
import { Logement } from "../../dataModel/generated/Logement";
import { useEffect, useState, useMemo, useRef } from "react";
import { StyledFloatingActionButton } from "../../components/StyledFloatingActionButton";
import { Edit } from "@mui/icons-material";
import { LogementOptions } from "../../utils/LogementOptions";
import { LogementAccessor } from "../../services/LogementAccessor";

export interface LogementPageProps {
    readonly viewKind: ViewKind
}

export const LogementPageBase = (props: LogementPageProps) => {
    const navigate = useNavigate();
    const data = useLoaderData() as undefined | {
        programme: string | null;
        logement: Logement;
        logEntries: ReadonlyArray<LogItem>;
        communes: ReadonlyArray<CommuneItem>;
        bailleurs: ReadonlyArray<BailleurItem>;
        forceProgramme: boolean | undefined;
        forceBailleur: boolean | undefined;
    };
    const isDirtyRef = useRef(false);
    const [currentLogement, setCurrentLogement] = useState<Logement | undefined>(undefined);

    // Initialize with loaded logement data, but not if the user has started editing
    useEffect(() => {
        if (data?.logement && !isDirtyRef.current) {
            setCurrentLogement(data.logement);
        }
    }, [data?.logement]);

    const logementAccessor = useMemo(
        () => new LogementAccessor(currentLogement, logement => {
            isDirtyRef.current = true;
            setCurrentLogement(logement)
        }),
        [currentLogement]
    );

    const logementOptions = useMemo(
        () => new LogementOptions(data?.communes, data?.bailleurs),
        [data?.communes, data?.bailleurs]
    );
    const handleEditClick = () => {
        navigate("./../edit");
    }
    return (<>
        <Stack sx={{p: 1}} spacing={1}>
            <Box sx={{p: 0}}>
                <Outlet context={{
                    logementAccessor: logementAccessor,
                    logementOptions: logementOptions,
                    logEntries: data?.logEntries,
                    currentLogement: currentLogement,
                    forceProgramme: data?.forceProgramme,
                    forceBailleur: data?.forceBailleur
                }}/>
            </Box>
        </Stack>
        { props.viewKind === ViewKind.Read &&
        <StyledFloatingActionButton color="primary" aria-label="modifier" onClick={handleEditClick}>
            <Edit />
        </StyledFloatingActionButton>}
    </>);
}

export interface LogementPageOutletContext {
    readonly logementAccessor: LogementAccessor;
    readonly logementOptions: LogementOptions;
    readonly logEntries: ReadonlyArray<LogItem> | undefined;
    readonly currentLogement: Logement | undefined;
    readonly forceProgramme: boolean | undefined;
    readonly forceBailleur: boolean | undefined;
}

export enum ViewKind {
    Read,
    Modify,
    New
}
